import React, { useState, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { withAutocompleteFieldShell } from './autocompleteFieldShell';

/**
 * Componente reutilizável de Autocomplete/Dropdown
 * 
 * @param {Object} props
 * @param {Array} props.options - Array de opções disponíveis
 * @param {string|number} props.value - Valor selecionado (ID da opção)
 * @param {Function} props.onChange - Callback quando o valor muda: (value) => void
 * @param {string} props.label - Label do campo
 * @param {boolean} props.disabled - Se o campo está desabilitado
 * @param {boolean} props.loading - Se está carregando
 * @param {Function} props.getOptionLabel - Função para obter o label da opção: (option) => string
 * @param {Function} props.getOptionValue - Função para obter o valor da opção: (option) => string|number
 * @param {Function} props.filterOptions - Função customizada para filtrar opções
 * @param {string} props.placeholder - Placeholder do campo
 * @param {Object} props.sx - Estilos customizados
 * @param {string} props.variant - Variante do TextField ('standard' | 'outlined' | 'filled')
 * @param {string} props.size - Tamanho do campo ('small' | 'medium')
 * @param {boolean} props.required - Se o campo é obrigatório
 * @param {boolean} props.error - Se há erro no campo
 * @param {string} props.helperText - Texto de ajuda/erro
 * @param {string} props.noOptionsText - Texto quando não há opções
 * @param {Function} props.renderOption - Função customizada para renderizar opções
 * @param {boolean} props.clearOnBlur - Se deve limpar ao perder o foco
 * @param {boolean} props.selectOnFocus - Se deve selecionar ao focar
 */
const SelectAutocomplete = ({
    options = [],
    value,
    onChange,
    label,
    disabled = false,
    loading = false,
    getOptionLabel = (option) => option?.name || '',
    getOptionValue = (option) => option?.id || '',
    filterOptions,
    placeholder,
    sx,
    variant = 'standard',
    size = 'medium',
    required = false,
    error = false,
    helperText,
    noOptionsText = 'Nenhuma opção disponível',
    renderOption,
    clearOnBlur = false,
    selectOnFocus = true,
    ...otherProps
}) => {
    const [inputValue, setInputValue] = useState('');

    // Encontrar a opção selecionada baseada no value
    const selectedOption = useMemo(() => {
        if (!value) return null;
        return options.find(option => getOptionValue(option) === value) || null;
    }, [value, options, getOptionValue]);

    // Filtrar opções baseado no input
    const filteredOptions = useMemo(() => {
        if (!inputValue) return options;
        
        const lowerInput = inputValue.toLowerCase();
        return options.filter(option => {
            const label = getOptionLabel(option).toLowerCase();
            return label.includes(lowerInput);
        });
    }, [options, inputValue, getOptionLabel]);

    const handleChange = (event, newValue) => {
        if (newValue) {
            const newValueId = getOptionValue(newValue);
            onChange(newValueId);
        } else {
            onChange('');
        }
    };

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
    };

    // Função padrão para renderizar opções
    const defaultRenderOption = (props, option) => (
        <li {...props} key={getOptionValue(option)}>
            {getOptionLabel(option)}
        </li>
    );

    return (
        <Autocomplete
            value={selectedOption}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={filterOptions ? filterOptions(filteredOptions) : filteredOptions}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => 
                getOptionValue(option) === getOptionValue(value)
            }
            disabled={disabled}
            loading={loading}
            loadingText="Carregando..."
            noOptionsText={noOptionsText}
            clearOnBlur={clearOnBlur}
            selectOnFocus={selectOnFocus}
            renderOption={renderOption || defaultRenderOption}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant={variant}
                    size={size}
                    required={required}
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            sx={withAutocompleteFieldShell(sx)}
            {...otherProps}
        />
    );
};

export default SelectAutocomplete;

